// Live link between the Takeoff canvas and the Estimate across windows / monitors of the same browser.
// Every successful takeoff save in the canvas broadcasts on a per-revision channel (RoomTakeoff.broadcastChange);
// any other window on that revision listens and re-reads. The one place the channel is named, so the sender
// (RoomTakeoff) and the listeners (the canvas's other windows, the Estimate) can never drift apart.
//
// Scope: windows of the SAME browser profile on one machine. Two people on two computers is a different mechanism
// (polling or server push) and is not part of this.
import { useEffect, useRef, useState } from "react";

export const takeoffChannelName = (revisionId) => `takeoff-rev-${revisionId}`;

// Calls onChange() (debounced) whenever ANOTHER window announces a takeoff change on this revision; returns
// { lastUpdate } — the time of the last refresh it triggered, for a "live" indicator.
export function useTakeoffLiveLink(revisionId, onChange, { delayMs = 250 } = {}) {
  const [lastUpdate, setLastUpdate] = useState(null);
  const latest = useRef(onChange);
  latest.current = onChange;
  useEffect(() => {
    if (!revisionId || typeof BroadcastChannel === "undefined") return undefined;
    const channel = new BroadcastChannel(takeoffChannelName(revisionId));
    let timer;
    channel.onmessage = (e) => {
      if (!e?.data) return;
      clearTimeout(timer);
      timer = setTimeout(() => { latest.current?.(); setLastUpdate(Date.now()); }, delayMs);
    };
    return () => { clearTimeout(timer); channel.close(); };
  }, [revisionId, delayMs]);
  return { lastUpdate };
}
